import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { Markdown } from "@tiptap/markdown";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { all, createLowlight } from "lowlight";
import { Image as ImageIcon, Redo, Undo } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { FormattingBubbleMenu } from "./formatting-bubble-menu";
import { ImagePickerPopover } from "./image-picker-popover";
import { LinkBubbleMenu } from "./link-bubble-menu";
import { MediaPickerPopover } from "./media-picker-popover";
import { PasteVideoLinkDialog } from "./paste-video-link-dialog";
import { SlashCommands } from "./slash-commands";
import type { UploadResult } from "./tiptap-file-upload-extension";
import { FileUpload } from "./tiptap-file-upload-extension";
import { Video } from "./tiptap-video-extension";
import { YouTube } from "./tiptap-youtube-extension";
import { VideoSourceModal } from "./video-source-modal";

const lowlight = createLowlight(all);

interface TiptapEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  /**
   * Handler for uploading pasted/dropped files
   * Should upload the files and return URLs
   */
  onUpload?: (files: File[]) => Promise<UploadResult[]>;
  /**
   * Called when an upload error occurs
   */
  onUploadError?: (error: string) => void;
  /**
   * Called when upload starts (for showing loading state)
   */
  onUploadStart?: (files: File[]) => void;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title: string;
}

function ToolbarButton({ onClick, isActive, disabled, children, title }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        isActive
          ? "bg-accent text-foreground"
          : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

export function TiptapEditor({
  content,
  onChange,
  placeholder = "Start writing...",
  onUpload,
  onUploadError,
  onUploadStart,
}: TiptapEditorProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showVideoPicker, setShowVideoPicker] = useState(false);
  const [showVideoSourceModal, setShowVideoSourceModal] = useState(false);
  const [showPasteLinkDialog, setShowPasteLinkDialog] = useState(false);
  // Track if we've done the initial content load to avoid feedback loops
  const hasInitializedRef = useRef(false);
  const lastContentRef = useRef(content);
  // Store editor and range from slash commands for media insertion
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pickerEditorRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pickerRangeRef = useRef<any>(null);

  const editor = useEditor({
    extensions: [
      Markdown,
      StarterKit.configure({
        codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 underline",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto rounded-lg",
        },
      }),
      Video.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto rounded-lg my-4",
        },
      }),
      YouTube.configure({
        HTMLAttributes: {
          class: "rounded-lg my-4",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "tiptap-table",
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      SlashCommands,
      // File upload extension for paste/drop support
      ...(onUpload
        ? [
            FileUpload.configure({
              onUpload,
              onError: onUploadError,
              onUploadStart,
            }),
          ]
        : []),
    ],
    content: "", // Start empty, we'll set content in useEffect
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastContentRef.current = html; // Track what we're sending to parent
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: "tiptap-editor px-6 py-4 focus:outline-none min-h-[400px]",
      },
    },
  });

  // Only sync content on initial load or when loading a completely different post
  // This prevents feedback loops that cause extra newlines when typing
  useEffect(() => {
    if (!editor) return;

    // First time initialization
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      lastContentRef.current = content;
      if (content) {
        editor.commands.setContent(content);
      }
      return;
    }

    // Only update if content changed externally (e.g., loading a different post)
    // and not from user typing (which would have already updated lastContentRef)
    if (content !== lastContentRef.current) {
      const currentHTML = editor.getHTML();
      // Check if this is a significant external change (not just normalization differences)
      if (content !== currentHTML && content !== lastContentRef.current) {
        lastContentRef.current = content;
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);

  // Listen for slash command events to open image/video pickers
  useEffect(() => {
    const handleOpenImagePicker = (e: CustomEvent) => {
      pickerEditorRef.current = e.detail?.editor;
      pickerRangeRef.current = e.detail?.range;
      setShowImagePicker(true);
    };

    const handleOpenVideoPicker = (e: CustomEvent) => {
      pickerEditorRef.current = e.detail?.editor;
      pickerRangeRef.current = e.detail?.range;
      setShowVideoPicker(true);
    };

    const handleOpenVideoSourceModal = (e: CustomEvent) => {
      pickerEditorRef.current = e.detail?.editor;
      pickerRangeRef.current = e.detail?.range;
      setShowVideoSourceModal(true);
    };

    window.addEventListener("openImagePicker", handleOpenImagePicker as EventListener);
    window.addEventListener("openVideoPicker", handleOpenVideoPicker as EventListener);
    window.addEventListener("openVideoSourceModal", handleOpenVideoSourceModal as EventListener);

    return () => {
      window.removeEventListener("openImagePicker", handleOpenImagePicker as EventListener);
      window.removeEventListener("openVideoPicker", handleOpenVideoPicker as EventListener);
      window.removeEventListener(
        "openVideoSourceModal",
        handleOpenVideoSourceModal as EventListener
      );
    };
  }, []);

  // Handle image selection from picker
  const handleImageSelect = useCallback(
    (url: string) => {
      const targetEditor = pickerEditorRef.current || editor;
      if (!targetEditor) return;

      const range = pickerRangeRef.current;
      if (range) {
        targetEditor.chain().focus().deleteRange(range).setImage({ src: url }).run();
      } else {
        targetEditor.chain().focus().setImage({ src: url }).run();
      }

      setShowImagePicker(false);
      pickerEditorRef.current = null;
      pickerRangeRef.current = null;
    },
    [editor]
  );

  // Handle video selection from picker
  const handleVideoSelect = useCallback(
    (url: string, type: "image" | "video") => {
      const targetEditor = pickerEditorRef.current || editor;
      if (!targetEditor) return;

      const range = pickerRangeRef.current;
      if (type === "video") {
        if (range) {
          targetEditor.chain().focus().deleteRange(range).setVideo({ src: url }).run();
        } else {
          targetEditor.chain().focus().setVideo({ src: url }).run();
        }
      } else {
        // In case an image is selected from the video picker
        if (range) {
          targetEditor.chain().focus().deleteRange(range).setImage({ src: url }).run();
        } else {
          targetEditor.chain().focus().setImage({ src: url }).run();
        }
      }

      setShowVideoPicker(false);
      pickerEditorRef.current = null;
      pickerRangeRef.current = null;
    },
    [editor]
  );

  // Extract YouTube video ID from various URL formats
  const extractYouTubeId = (url: string): string | null => {
    try {
      const parsed = new URL(url);

      // youtube.com format
      if (parsed.hostname.includes("youtube.com")) {
        return parsed.searchParams.get("v");
      }

      // youtu.be format
      if (parsed.hostname.includes("youtu.be")) {
        return parsed.pathname.substring(1);
      }

      return null;
    } catch {
      return null;
    }
  };

  // Handle YouTube URL submission
  const handlePasteVideoLink = useCallback(
    (url: string) => {
      const targetEditor = pickerEditorRef.current || editor;
      if (!targetEditor) return;

      const youtubeId = extractYouTubeId(url);
      const range = pickerRangeRef.current;

      if (youtubeId) {
        // It's a YouTube video
        if (range) {
          targetEditor.chain().focus().deleteRange(range).setYouTube({ videoId: youtubeId }).run();
        } else {
          targetEditor.chain().focus().setYouTube({ videoId: youtubeId }).run();
        }
      } else {
        // Treat as a direct video file URL
        if (range) {
          targetEditor.chain().focus().deleteRange(range).setVideo({ src: url }).run();
        } else {
          targetEditor.chain().focus().setVideo({ src: url }).run();
        }
      }

      setShowPasteLinkDialog(false);
      pickerEditorRef.current = null;
      pickerRangeRef.current = null;
    },
    [editor]
  );

  // Handle "Upload Video File" button from source modal
  const handleUploadVideoFile = useCallback(() => {
    setShowVideoSourceModal(false);
    setShowVideoPicker(true);
  }, []);

  // Handle "Paste Link" button from source modal
  const handlePasteVideoLink2 = useCallback(() => {
    setShowVideoSourceModal(false);
    setShowPasteLinkDialog(true);
  }, []);

  // Drag and drop handlers for visual feedback
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the container (not entering a child)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(() => {
    setIsDragging(false);
    // The ProseMirror plugin handles the actual drop
  }, []);

  if (!editor) {
    return (
      <div className="border border-border rounded-lg bg-card">
        <div className="h-12 border-b border-border bg-secondary/50" />
        <div className="px-6 py-4 min-h-[400px] animate-pulse">
          <div className="h-4 bg-secondary rounded w-3/4 mb-4" />
          <div className="h-4 bg-secondary rounded w-1/2" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative border rounded-lg bg-card overflow-hidden transition-colors ${
        isDragging ? "border-primary border-2 bg-primary/5" : "border-border"
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-primary">
            <ImageIcon className="h-12 w-12" />
            <span className="text-sm font-medium">Drop to upload</span>
          </div>
        </div>
      )}

      {/* Minimal toolbar - only undo/redo */}
      <div className="flex items-center gap-0.5 px-3 py-2 border-b border-border bg-secondary/50">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Y)"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} />

      {/* Formatting bubble menu - shows when selecting text */}
      <FormattingBubbleMenu editor={editor} />

      {/* Link bubble menu - shows when clicking on a link */}
      <LinkBubbleMenu editor={editor} />

      {/* Image picker dialog - triggered by slash commands */}
      <ImagePickerPopover
        onSelect={handleImageSelect}
        align="center"
        open={showImagePicker}
        onOpenChange={setShowImagePicker}
      >
        <span />
      </ImagePickerPopover>

      {/* Video picker dialog - triggered by slash commands */}
      <MediaPickerPopover
        onSelect={handleVideoSelect}
        mediaType="video"
        align="center"
        open={showVideoPicker}
        onOpenChange={setShowVideoPicker}
      >
        <span />
      </MediaPickerPopover>

      {/* Video source modal - choose between upload or paste link */}
      <VideoSourceModal
        open={showVideoSourceModal}
        onOpenChange={setShowVideoSourceModal}
        onUpload={handleUploadVideoFile}
        onPasteLink={handlePasteVideoLink2}
      />

      {/* Paste video link dialog */}
      <PasteVideoLinkDialog
        open={showPasteLinkDialog}
        onOpenChange={setShowPasteLinkDialog}
        onSubmit={handlePasteVideoLink}
      />
    </div>
  );
}

// Helper to convert HTML to Markdown (for export)
export function htmlToMarkdown(html: string): string {
  let markdown = html;

  // Headers
  markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, "# $1\n\n");
  markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, "## $1\n\n");
  markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, "### $1\n\n");

  // Bold and italic
  markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, "**$1**");
  markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, "**$1**");
  markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, "*$1*");
  markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, "*$1*");
  markdown = markdown.replace(/<s[^>]*>(.*?)<\/s>/gi, "~~$1~~");

  // Links
  markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, "[$2]($1)");

  // Images
  markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*\/?>/gi, "![$2]($1)");
  markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*\/?>/gi, "![]($1)");

  // Code blocks
  markdown = markdown.replace(
    /<pre[^>]*><code[^>]*>([\s\S]*?)<\/code><\/pre>/gi,
    "```\n$1\n```\n\n"
  );

  // Inline code
  markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, "`$1`");

  // Blockquotes
  markdown = markdown.replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, (_, content) => {
    const lines = content
      .replace(/<\/?p[^>]*>/gi, "")
      .split("\n")
      .filter((l: string) => l.trim());
    return lines.map((l: string) => `> ${l.trim()}`).join("\n") + "\n\n";
  });

  // Lists
  markdown = markdown.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, content) => {
    return content.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, "- $1\n").trim() + "\n\n";
  });
  markdown = markdown.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, content) => {
    let index = 1;
    return (
      content
        .replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, () => {
          return `${index++}. ` + "$1\n";
        })
        .trim() + "\n\n"
    );
  });

  // Paragraphs
  markdown = markdown.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, "$1\n\n");

  // Horizontal rules
  markdown = markdown.replace(/<hr[^>]*\/?>/gi, "\n---\n\n");

  // Line breaks
  markdown = markdown.replace(/<br[^>]*\/?>/gi, "\n");

  // Remove remaining HTML tags
  markdown = markdown.replace(/<[^>]+>/g, "");

  // Decode HTML entities
  markdown = markdown.replace(/&amp;/g, "&");
  markdown = markdown.replace(/&lt;/g, "<");
  markdown = markdown.replace(/&gt;/g, ">");
  markdown = markdown.replace(/&quot;/g, '"');
  markdown = markdown.replace(/&#39;/g, "'");
  markdown = markdown.replace(/&nbsp;/g, " ");

  // Clean up extra whitespace
  markdown = markdown.replace(/\n{3,}/g, "\n\n");
  markdown = markdown.trim();

  return markdown;
}

// Helper to convert Markdown to HTML (for import)
export function markdownToHtml(markdown: string): string {
  let html = markdown;

  // Code blocks first (to prevent processing inside them)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre><code class="language-${lang || "text"}">${code.trim()}</code></pre>`;
  });

  // Headers
  html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
  html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
  html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

  // Bold and italic
  html = html.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
  html = html.replace(/~~(.*?)~~/g, "<s>$1</s>");

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Inline code
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

  // Blockquotes
  html = html.replace(/^> (.*$)/gim, "<blockquote><p>$1</p></blockquote>");

  // Unordered lists
  html = html.replace(/^\s*[-*] (.*$)/gim, "<li>$1</li>");
  html = html.replace(/(<li>.*<\/li>\n?)+/g, "<ul>$&</ul>");

  // Ordered lists
  html = html.replace(/^\s*\d+\. (.*$)/gim, "<li>$1</li>");

  // Horizontal rules
  html = html.replace(/^---$/gim, "<hr />");

  // Paragraphs
  html = html
    .split(/\n\n+/)
    .map((block) => {
      block = block.trim();
      if (
        !block ||
        block.startsWith("<h") ||
        block.startsWith("<ul") ||
        block.startsWith("<ol") ||
        block.startsWith("<pre") ||
        block.startsWith("<blockquote") ||
        block.startsWith("<hr")
      ) {
        return block;
      }
      return `<p>${block.replace(/\n/g, "<br />")}</p>`;
    })
    .join("\n");

  return html;
}
